import React, { useState } from 'react'
import type { AgentTeam } from '../../hooks/useAgentTeams'
import TeamItem from './TeamItem'
import TeamCreateModal from './TeamCreateModal'
import DeleteConfirmModal from '../knowledge/DeleteConfirmModal'
import { useAppLanguage } from '../../i18n'

type TeamsSidebarProps = {
  teams: AgentTeam[]
  loading: boolean
  selectedId: number | 'orphan' | null
  onSelect: (id: number | 'orphan' | null) => void
  totalAgents: number
  orphanCount: number
  agentCountByTeam: Record<number, number>
  onCreate: (title: string) => Promise<boolean>
  onRename: (id: number, title: string) => Promise<boolean>
  onDelete: (id: number) => Promise<boolean>
  // Lifted state for onboarding
  createModalOpen?: boolean
  onCreateModalOpenChange?: (open: boolean) => void
}

/**
 * Sidebar des équipes d'agents avec liste, création et actions
 * Suit le même pattern que CollectionsSidebar
 */
export default function TeamsSidebar({
  teams,
  loading,
  selectedId,
  onSelect,
  totalAgents,
  orphanCount,
  agentCountByTeam,
  onCreate,
  onRename,
  onDelete,
  createModalOpen: externalCreateModalOpen,
  onCreateModalOpenChange,
}: TeamsSidebarProps) {
  const { t } = useAppLanguage()
  const [internalCreateModalOpen, setInternalCreateModalOpen] = useState(false)
  
  // Use external state if provided, otherwise use internal state
  const createModalOpen = externalCreateModalOpen ?? internalCreateModalOpen
  const setCreateModalOpen = onCreateModalOpenChange ?? setInternalCreateModalOpen
  const [deleteTarget, setDeleteTarget] = useState<AgentTeam | null>(null)

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return false
    const success = await onDelete(deleteTarget.id)
    if (success) {
      // Si on supprime l'équipe sélectionnée, désélectionner
      if (selectedId === deleteTarget.id) {
        onSelect(null)
      }
    }
    return success
  }

  const handleCreate = async (title: string): Promise<boolean> => {
    try {
      await onCreate(title)
      return true
    } catch {
      return false
    }
  }

  return (
    <>
      <aside className="w-72 shrink-0">
        <div className="sticky top-6 space-y-4 rounded-3xl border border-emerald-100/70 bg-white/90 p-4 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300">
              {t.agents.teams.header} ({teams.length})
            </h2>
            <button
              type="button"
              onClick={() => setCreateModalOpen(true)}
              className="rounded-full border border-emerald-200/70 p-1.5 text-emerald-600 transition hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/40"
              title={t.agents.teams.newTeam}
              data-onboarding="add-team-btn"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Liste */}
          <div className="space-y-1">
            {/* Tous les agents */}
            <button
              type="button"
              onClick={() => onSelect(null)}
              className={`w-full flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${
                selectedId === null
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                  : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800/60'
              }`}
            >
              <span className="font-medium">{t.agents.teams.allAgents}</span>
              <span className="rounded-full bg-zinc-200/80 px-2 py-0.5 text-xs font-semibold text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300">
                {totalAgents}
              </span>
            </button>

            {/* Équipes */}
            {loading ? (
              <div className="animate-pulse space-y-2 py-2">
                <div className="h-8 rounded-xl bg-emerald-100/60 dark:bg-emerald-900/40" />
                <div className="h-8 rounded-xl bg-emerald-100/50 dark:bg-emerald-900/30" />
                <div className="h-8 rounded-xl bg-emerald-100/40 dark:bg-emerald-900/20" />
              </div>
            ) : (
              teams.map(team => (
                <TeamItem
                  key={team.id}
                  team={team}
                  agentCount={agentCountByTeam[team.id] || 0}
                  isSelected={selectedId === team.id}
                  onSelect={() => onSelect(team.id)}
                  onRename={(newTitle) => onRename(team.id, newTitle)}
                  onDelete={() => setDeleteTarget(team)}
                />
              ))
            )}

            {/* Séparateur */}
            {(teams.length > 0 || orphanCount > 0) && (
              <div className="my-2 border-t border-zinc-200/70 dark:border-zinc-700/70" />
            )}

            {/* Orphelins */}
            <button
              type="button"
              onClick={() => onSelect('orphan')}
              className={`w-full flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${
                selectedId === 'orphan'
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                  : 'text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800/60'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-500" />
                <span className="font-medium">{t.agents.teams.noTeam}</span>
              </div>
              <span className="rounded-full bg-amber-200/80 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/60 dark:text-amber-200">
                {orphanCount}
              </span>
            </button>
          </div>
        </div>
      </aside>

      {/* Modal création */}
      <TeamCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreate}
      />

      {/* Modal suppression */}
      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title={t.agents.teams.deleteTitle}
        itemName={deleteTarget?.title || ''}
        itemId={deleteTarget?.id || 0}
        warningMessage={t.agents.teams.deleteWarning}
      />
    </>
  )
}
